import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, X, GripVertical, Eye, EyeOff } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import ImageUpload from "../../components/ImageUpload";
import SaveButton from "../../components/admin/SaveButton";
import { useUploadStatus } from "../../context/UploadStatusContext";

const empty = {
    name: "", slug: "", description: "",
    cover_image_url: "", banner_image_url: "", parent_slug: "", order: 0, is_active: true,
};

export default function AdminCategories() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const { isUploading } = useUploadStatus();

    // parent="*" returns ALL categories (top-level + sub-categories) for admin management
    const load = () => api.get("/categories?active_only=false&parent=*").then((r) => setItems(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    // Top-level categories are the only valid parents
    const topLevel = items.filter((c) => !c.parent_slug);

    const openNew = () => { setEditing("new"); setForm(empty); };
    const openEdit = (c) => { setEditing(c.id); setForm({ ...c }); };
    const close = () => { setEditing(null); setForm(empty); };

    const save = async (e) => {
        e.preventDefault();
        if (isUploading) return;
        const payload = { ...form, order: parseInt(form.order) || 0 };
        try {
            if (editing === "new") await api.post("/categories", payload);
            else await api.put(`/categories/${editing}`, payload);
            toast.success("Saved"); close(); load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this category? Cakes in this category will remain but won't appear on the gallery landing.")) return;
        try { await api.delete(`/categories/${id}`); toast.success("Deleted"); load(); }
        catch { toast.error("Delete failed"); }
    };

    const toggleActive = async (c) => {
        try {
            await api.put(`/categories/${c.id}`, { ...c, is_active: !c.is_active });
            toast.success(c.is_active ? "Disabled — hidden from site" : "Enabled — now visible");
            load();
        } catch { toast.error("Update failed"); }
    };

    return (
        <div data-testid="admin-categories">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="font-display text-3xl">Gallery Categories</h2>
                    <p className="text-sm text-ink-soft mt-1">
                        Each category is a big rectangle on <code className="text-xs">/gallery</code>.
                        Click a category to see its portfolio of cakes.
                    </p>
                </div>
                <button onClick={openNew} className="btn-pink" data-testid="add-category"><Plus size={14} /> New</button>
            </div>

            <div className="space-y-2">
                {items.map((c, i) => (
                    <div key={c.id} className="bg-white border border-[#EAE3DE] p-4 flex items-center gap-4" data-testid={`category-row-${i}`}>
                        <GripVertical size={14} className="text-ink-soft/40" />
                        {c.cover_image_url ? (
                            <img src={c.cover_image_url} alt={c.name} className="w-16 h-20 object-cover" />
                        ) : (
                            <div className="w-16 h-20 bg-blush-pale flex items-center justify-center text-[9px] text-ink-soft">no image</div>
                        )}
                        <div className="flex-1 min-w-0">
                            <p className="font-display text-lg">
                                {c.parent_slug && <span className="text-ink-soft/50">↳ </span>}
                                {c.name}
                                {c.parent_slug && (
                                    <span className="ml-2 text-[9px] tracking-[0.2em] uppercase bg-blush-pale text-blush px-1.5 py-0.5">
                                        under {c.parent_slug}
                                    </span>
                                )}
                            </p>
                            <p className="text-xs text-ink-soft">/gallery/{c.slug} · order {c.order}</p>
                            {c.description && <p className="text-xs text-ink-soft mt-1 line-clamp-1">{c.description}</p>}
                        </div>
                        <span className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 ${c.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                            {c.is_active ? "Active" : "Hidden"}
                        </span>
                        <div className="flex gap-3 items-center ml-2">
                            <button onClick={() => toggleActive(c)} className={c.is_active ? "text-green-600 hover:text-blush" : "text-gray-400 hover:text-blush"} title={c.is_active ? "Disable (hide from site)" : "Enable (show on site)"} data-testid={`toggle-category-${i}`}>
                                {c.is_active ? <Eye size={16} /> : <EyeOff size={16} />}
                            </button>
                            <button onClick={() => openEdit(c)} data-testid={`edit-category-${i}`}><Edit size={16} /></button>
                            <button onClick={() => remove(c.id)} data-testid={`delete-category-${i}`}><Trash2 size={16} /></button>
                        </div>
                    </div>
                ))}
                {items.length === 0 && <p className="text-center text-ink-soft py-12">No categories yet — click "New" to create your first.</p>}
            </div>

            {editing && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                    <div className="bg-white w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="font-display text-2xl">{editing === "new" ? "New" : "Edit"} Category</h3>
                            <button onClick={close}><X size={20} /></button>
                        </div>
                        <form onSubmit={save} className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Name</label>
                                    <input className="w-full px-4 py-3 border border-[#EAE3DE]" placeholder="e.g. Wedding" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                                </div>
                                <div>
                                    <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Slug (URL)</label>
                                    <input className="w-full px-4 py-3 border border-[#EAE3DE]" placeholder="wedding" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required />
                                </div>
                            </div>
                            <div>
                                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Description (shown under the tile)</label>
                                <textarea className="w-full px-4 py-3 border border-[#EAE3DE]" rows={2} placeholder="Multi-tier centrepieces tailored to your love story." value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Parent category (optional)</label>
                                <select
                                    className="w-full px-4 py-3 border border-[#EAE3DE] bg-white"
                                    value={form.parent_slug || ""}
                                    onChange={(e) => setForm({ ...form, parent_slug: e.target.value })}
                                    data-testid="category-parent"
                                >
                                    <option value="">— Top-level (shows on /gallery) —</option>
                                    {topLevel
                                        .filter((c) => c.slug !== form.slug)
                                        .map((c) => (
                                            <option key={c.id} value={c.slug}>Under: {c.name}</option>
                                        ))}
                                </select>
                                <p className="text-[11px] text-ink-soft mt-1 italic">
                                    Set a parent to make this a sub-category (e.g. "Birthday Female" under "Birthday").
                                    Parent tiles show their sub-categories instead of cakes.
                                </p>
                            </div>
                            <ImageUpload value={form.cover_image_url} onChange={(v) => setForm({ ...form, cover_image_url: v })} label="Cover Image (portrait, ~1200×1500) — shown as the tile" testIdPrefix="category-image" />
                            <ImageUpload value={form.banner_image_url} onChange={(v) => setForm({ ...form, banner_image_url: v })} label="Banner Image (wide, optional) — shown at the top of the category page. Leave empty to reuse the cover image." testIdPrefix="category-banner-image" />
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Sort order</label>
                                    <input type="number" className="w-full px-4 py-3 border border-[#EAE3DE]" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} />
                                </div>
                                <label className="flex items-end gap-2 pb-3">
                                    <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                                    <span className="text-sm">Active (visible on /gallery)</span>
                                </label>
                            </div>
                            <p className="text-[11px] text-ink-soft italic">
                                Cakes are linked to a category by matching the category <strong>Name</strong> exactly (e.g. "Wedding")
                                to the "Category" field on each Gallery Cake.
                            </p>
                            <SaveButton>Save Category</SaveButton>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
